import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Play, Stop, Timer, X } from "@phosphor-icons/react";

function formatElapsed(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export default function TaskTimer({ onStopped }) {
  const [startedAt, setStartedAt] = useState(null); // ms epoch
  const [label, setLabel] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const tickRef = useRef();
  const labelSaveRef = useRef();
  const lastPushedLabel = useRef("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/timer");
      setStartedAt(data.started_at ? new Date(data.started_at).getTime() : null);
      setLabel(data.label || "");
      lastPushedLabel.current = data.label || "";
    } catch { /* transient — will re-sync on next call */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!startedAt) return undefined;
    const tick = () => setElapsed((Date.now() - startedAt) / 1000);
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
  }, [startedAt]);

  // Debounced label sync while the timer is running
  useEffect(() => {
    if (!startedAt) return undefined;
    if (label === lastPushedLabel.current) return undefined;
    if (labelSaveRef.current) clearTimeout(labelSaveRef.current);
    labelSaveRef.current = setTimeout(async () => {
      try {
        await api.put("/timer", { label });
        lastPushedLabel.current = label;
      } catch { /* transient — will re-sync on next call */ }
    }, 500);
    return () => clearTimeout(labelSaveRef.current);
  }, [label, startedAt]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/timer/start", { label });
      setStartedAt(new Date(data.started_at).getTime());
      lastPushedLabel.current = data.label || "";
      toast.success(data.label ? `Timer started · ${data.label}` : "Timer started");
    } catch { toast.error("Could not start timer"); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/timer/stop");
      setStartedAt(null);
      setElapsed(0);
      setLabel("");
      lastPushedLabel.current = "";
      onStopped?.({ minutes: data.minutes, label: data.label || "" });
    } catch { toast.error("Could not stop timer"); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm("Discard the running timer?")) return;
    setBusy(true);
    try {
      await api.post("/timer/stop");
      setStartedAt(null);
      setElapsed(0);
      setLabel("");
      lastPushedLabel.current = "";
      toast("Timer discarded");
    } catch { toast.error("Could not discard"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="task-timer" className="bg-white rounded-2xl border border-slate-200/70 p-5">
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${startedAt ? "al-gradient text-white" : "bg-slate-100 text-slate-500"}`}>
          <Timer size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Task Timer</div>
          <div className="text-2xl font-semibold tabular-nums leading-tight mt-0.5" data-testid="task-timer-display">
            {startedAt ? formatElapsed(elapsed) : "00:00:00"}
          </div>
        </div>
        {!startedAt ? (
          <button data-testid="task-timer-start" onClick={start} disabled={busy} className="al-gradient text-white px-5 py-2.5 rounded-full font-medium inline-flex items-center gap-2 disabled:opacity-60 active:scale-95 transition-transform shrink-0">
            <Play size={16} weight="fill"/> Start
          </button>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="task-timer-stop" onClick={stop} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full font-medium inline-flex items-center gap-2 disabled:opacity-60 active:scale-95 transition-transform">
              <Stop size={16} weight="fill"/> Stop &amp; log
            </button>
            <button data-testid="task-timer-discard" onClick={discard} disabled={busy} title="Discard" className="text-slate-400 hover:text-red-500 p-2 rounded-full">
              <X size={16} weight="bold"/>
            </button>
          </div>
        )}
      </div>
      <input
        data-testid="task-timer-label"
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={startedAt ? "Label this task (e.g. Truck unload)…" : "Name your task before starting (optional)…"}
        className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
      />
    </div>
  );
}
